import {
  all, put, call, select,
} from 'lib/redux';
import * as types from 'constants/ActionTypes';
import getEstimatedCostService from 'services/getEstimatedCost.service';
import checkAddonCompatiblilityService from 'services/checkAddonCompatiblility.service';
import {
  PROPERTY_DETAILS_MODULE, KEY, INITIALIZATION_VECTOR, CLIENT_ERROR_RESPONSES, PRODUCT_IDS,
} from 'constants/AppConstants';
import { ENABLE_GEOMNI_ESX_ADDON_LOGIC } from 'constants/FeatureFlag.constants';
import getValidatorFieldsService from 'services/getValidatorFields.service';
import upgradeReportService from 'services/upgradeReport.service';
import upgradedReportService from 'services/upgradedReport.service';
import { ORDER_ROUTE, UPGRADED_REPORT_ROUTE } from 'constants/RouteConstants';
import { decodeAndDecrypt, getProductType } from 'utils/app.utils';
import history from 'store/CreateHistory';
import {
  getBillingInformationSaga, getUserProductsSaga, getControlStatesSaga, getOrderPreferencesSaga,
  getInitParametersSaga, getSubscriptionDashboardDataSaga,
} from 'features/userData/UserData.saga';
import { isSuperUserSelector, featureFlagSelector, isSubscriptionDashboardV2EnabledSelector } from 'utils/appSelectors.utils';
import { logError } from 'utils/logger.utils';
import { ESX_COMPATIBLE_PRODUCTS } from 'features/order/components/Product/ProductList.constants';
import { REGULAR_DELIVERY_PRODUCT_ID } from 'features/order/Order.constants';
import { mergePrimarySubscriptionProductsSaga } from 'features/order/Order.saga';
import { setSubscriptionEstimatedTotal } from 'features/order/Order.actions';
import {
  getWallsAddOnId,
  estimatedCostSelector,
  addProductToProductSets,
  substitutionAndCustomizationAttribute,
  upgradeProductSetSelector,
  shouldRedirectFromUpgrade,
  buildEsxUpgradeProductSets,
  buildUpgradeSubscriptionEstimatedTotalText,
} from './FinalizeOrder.utils';
import { updateCreditCardConsent, updatePaymentInfo } from './FinalizeOrder.actions';

function* shouldUseSubscriptionUpgradeEstimatedTotal() {
  const isSubscriptionDashboardV2Enabled = yield select(isSubscriptionDashboardV2EnabledSelector);
  const upgradeFromReportId = yield select(({ finalizeOrder }) => finalizeOrder.upgradeFromReportId);
  return Boolean(isSubscriptionDashboardV2Enabled && upgradeFromReportId);
}

export function* refreshUpgradeSubscriptionEstimatedTotalSaga() {
  const { productSets } = yield select(({ placeOrder }) => placeOrder);
  const { primarySubscriptionProductsWithUsage } = yield select(({ order }) => order);
  const currencyTypeId = yield select(({ userData }) => userData?.initParameters?.currencyTypeId);

  const estimatedTotalText = buildUpgradeSubscriptionEstimatedTotalText({
    productSet: productSets?.[0],
    primarySubscriptionProductsWithUsage,
    currencyTypeId,
  });

  if (estimatedTotalText == null) {
    yield put(setSubscriptionEstimatedTotal(null));
    return false;
  }

  yield put(setSubscriptionEstimatedTotal(estimatedTotalText));
  return true;
}

export function* getEstimatedCostSaga() {
  try {
    if (yield call(shouldUseSubscriptionUpgradeEstimatedTotal)) {
      const usedSubscriptionLocalPricing = yield call(refreshUpgradeSubscriptionEstimatedTotalSaga);
      if (usedSubscriptionLocalPricing) {
        // Clear any leftover loading flag from a cancelled non-subscription estimated-cost call.
        yield put({ type: types.FETCHED_ESTIMATED_COST });
        return { ok: true };
      }
      // Non-subscription upgrade target: fall through to estimated-cost API (products API pricing).
    }

    yield put({ type: types.FETCHING_ESTIMATED_COST });
    const { upgradeFromReportId } = yield select(({ finalizeOrder }) => (finalizeOrder));

    // Property Data now goes through the regular API flow for proper currency conversion and tax calculation

    const requestBody = yield select(estimatedCostSelector);
    if (upgradeFromReportId) Object.assign(requestBody, { upgradeFromReportId: parseInt(upgradeFromReportId, 10) });
    const response = yield call(getEstimatedCostService, requestBody);

    if (!response.ok) {
      const error = new Error(response.json.errorDescription);
      error.errorCode = response.json.errorCode;
      throw error;
    }
    const estimatedCostResponse = response.json;
    yield put({ type: types.GET_ESTIMATED_COST_SUCCESS, estimatedCostResponse });
    yield put({ type: types.FETCHED_ESTIMATED_COST });

    return { ok: true };
  } catch (error) {
    logError({
      message: 'Error in getEstimatedCostSaga',
      error: error.message,
      errorCode: error.errorCode,
      upgradeFromReportId: error.upgradeFromReportId,
    });
    yield put({ type: types.FETCHED_ESTIMATED_COST });
    return { ok: false, error };
  }
}

const hasPrimaryProductSet = (productSets, primaryProductId) => productSets.some(
  (ps) => Number(ps.primaryProductId) === Number(primaryProductId),
);

const addBaseProductSets = ({
  productSets,
  roofProduct,
  roofDeliveryProductId,
  wallsProduct,
  wallsDeliveryProductId,
  isSubscriptionFlow,
}) => {
  if (roofProduct) addProductToProductSets(productSets, [], roofProduct.ProductID, roofDeliveryProductId);
  const isSameProduct = roofProduct && wallsProduct && roofProduct.ProductID === wallsProduct.ProductID;
  const shouldAddWalls = wallsProduct && (!isSubscriptionFlow || !isSameProduct);
  if (shouldAddWalls) addProductToProductSets(productSets, [], wallsProduct.ProductID, wallsDeliveryProductId);
};

const ensureEsxCompatibleRoofSet = ({
  productSets,
  hasEsx,
  roofProduct,
  roofIsEsxCompatible,
  roofDeliveryProductId,
}) => {
  if (!hasEsx || !roofProduct || !roofIsEsxCompatible) return;
  const hasCompatiblePrimarySet = productSets.some(
    (ps) => ESX_COMPATIBLE_PRODUCTS.includes(Number(ps.primaryProductId)),
  );
  const hasRoofPrimarySet = hasPrimaryProductSet(productSets, roofProduct.ProductID);
  if (!hasCompatiblePrimarySet && !hasRoofPrimarySet) {
    addProductToProductSets(productSets, [], roofProduct.ProductID, roofDeliveryProductId);
  }
};

const attachEsxAddonToRoofSet = ({
  productSets,
  roofProduct,
  roofIsEsxCompatible,
  roofDeliveryProductId,
  isSubscriptionFlow,
}) => {
  if (!roofProduct || !roofIsEsxCompatible) return;
  let roofSet = productSets.find((ps) => Number(ps.primaryProductId) === Number(roofProduct.ProductID));
  if (!roofSet) {
    addProductToProductSets(productSets, [], roofProduct.ProductID, roofDeliveryProductId);
    roofSet = productSets.find((ps) => Number(ps.primaryProductId) === Number(roofProduct.ProductID));
  }
  if (!roofSet) return;
  if (!roofSet.addOnProductIds.includes(PRODUCT_IDS.ESX)) {
    roofSet.addOnProductIds.push(PRODUCT_IDS.ESX);
  }
  // Classic roof + ESX: product set must use regular delivery. Subscription V2 keeps upgraded delivery with ESX.
  if (!isSubscriptionFlow) {
    roofSet.deliveryProductId = REGULAR_DELIVERY_PRODUCT_ID;
    roofSet.deliveryProductType = getProductType(REGULAR_DELIVERY_PRODUCT_ID);
  }
};

const addOtherProductsToSets = ({
  productSets,
  otherProducts,
  roofProduct,
  roofIsEsxCompatible,
  roofDeliveryProductId,
  isSubscriptionFlow,
}) => {
  Object.entries(otherProducts).forEach(([primaryProductId, deliveryProductId]) => {
    const productIdNum = Number(primaryProductId);
    if (productIdNum === PRODUCT_IDS.ESX) {
      attachEsxAddonToRoofSet({
        productSets,
        roofProduct,
        roofIsEsxCompatible,
        roofDeliveryProductId,
        isSubscriptionFlow,
      });
      return;
    }
    addProductToProductSets(productSets, [], productIdNum, deliveryProductId);
  });
};

function* updateProductSetsSaga() {
  try {
    const orderState = yield select(({ order }) => order);
    const {
      selectedPrimaryProduct: roofProduct,
      selectedPrimaryDeliveryProduct: roofDeliveryProductId,
      selectedWallsProduct: wallsProduct,
      selectedWallsDeliveryProduct: wallsDeliveryProductId,
      selectedOtherProducts: otherProducts,
    } = orderState;
    const isESXEnabled = yield select(featureFlagSelector(ENABLE_GEOMNI_ESX_ADDON_LOGIC));
    const isSubscriptionFlow = Boolean(orderState.subscriptionPlanMode || (orderState.primarySubscriptionProductsWithUsage?.length > 0));

    const productSets = [];

    if (roofProduct && wallsProduct) {
      const wallsAddOnId = getWallsAddOnId(roofProduct, wallsProduct.ProductID);

      const { ok, json } = yield call(checkAddonCompatiblilityService, wallsProduct.ProductID, wallsAddOnId);

      if (ok && json.isAddonCompatible) addProductToProductSets(productSets, [wallsAddOnId], roofProduct.ProductID, roofDeliveryProductId);
    }

    if (productSets.length === 0) {
      addBaseProductSets({
        productSets,
        roofProduct,
        roofDeliveryProductId,
        wallsProduct,
        wallsDeliveryProductId,
        isSubscriptionFlow,
      });
    }

    const hasEsx = isESXEnabled && Object.hasOwn(otherProducts, PRODUCT_IDS.ESX);
    const roofPrimaryId = roofProduct ? Number(roofProduct.ProductID) : null;
    const roofIsEsxCompatible = roofPrimaryId != null && ESX_COMPATIBLE_PRODUCTS.includes(roofPrimaryId);
    ensureEsxCompatibleRoofSet({
      productSets,
      hasEsx,
      roofProduct,
      roofIsEsxCompatible,
      roofDeliveryProductId,
    });
    addOtherProductsToSets({
      productSets,
      otherProducts,
      roofProduct,
      roofIsEsxCompatible,
      roofDeliveryProductId,
      isSubscriptionFlow,
    });

    yield put({ type: types.UPDATE_PRODUCT_SETS, productSets });
    return { ok: true };
  } catch (error) {
    logError({
      message: 'Error in updateProductSetsSaga',
      error: error.message,
      errorCode: error.errorCode,
    });
    return { ok: false, error };
  }
}

function* getControlStates() {
  try {
    yield getControlStatesSaga({ moduleName: PROPERTY_DETAILS_MODULE });
    const controlStates = yield select(({ userData }) => (userData.controlStates));
    const hasRequiredField = controlStates?.homeOwner && !controlStates?.homeOwner.hidden;
    if (hasRequiredField) yield put({ type: types.EXPAND_PANEL, isExpanded: true, panelName: 'claimInformation' });
    return { ok: true };
  } catch (error) {
    logError({
      message: 'Error in getControlStates',
      error: error.message,
      errorCode: error.errorCode,
    });
    return { ok: false, error };
  }
}

function* getOrderPreferences() {
  try {
    yield getOrderPreferencesSaga();
    return { ok: true };
  } catch (error) {
    logError({
      message: 'Error in getOrderPreferences',
      error: error.message,
      errorCode: error.errorCode,
    });
    return { ok: false, error };
  }
}

function* getBillPayerInfo(placeOrderForOtherUser = false) {
  const isSuperUser = yield select(isSuperUserSelector);
  if (isSuperUser && placeOrderForOtherUser) yield put({ type: types.RESET_BILLING_INFO });
  try {
    if (!(isSuperUser && placeOrderForOtherUser)) {
      yield getBillingInformationSaga({ isUpdated: true });

      const billPayerInfoResponse = yield select(({ userData }) => (userData.billingInformation));
      const isPaymentInfoUpdated = yield select(({ finalizeOrder }) => (finalizeOrder.paymentInfo.isUpdated));

      const paymentInfo = {
        firstName: billPayerInfoResponse.creditCard.cardFirstName,
        lastName: billPayerInfoResponse.creditCard.cardLastName,
        cardNumber: billPayerInfoResponse.creditCard.creditCardNumber,
        cardType: billPayerInfoResponse.creditCard.creditCardType,
        expirationDateMonth: billPayerInfoResponse.creditCard.cardExpMonth,
        expirationDateYear: billPayerInfoResponse.creditCard.cardExpYear,
      };

      if (!isPaymentInfoUpdated) yield put(updatePaymentInfo(paymentInfo));
      yield put(updateCreditCardConsent(billPayerInfoResponse.creditCard.ccConsent));
    }
    return { ok: true };
  } catch (error) {
    logError({
      message: 'Error in getBillPayerInfo',
      error: error.message,
      errorCode: error.errorCode,
      placeOrderForOtherUser,
    });
    return { ok: false, error };
  }
}

function* getValidatorFieldsSaga() {
  try {
    const userId = (yield select(({ finalizeOrder, userData }) => finalizeOrder.userId || userData.initParameters.customerId));
    const response = yield call(getValidatorFieldsService, userId);

    if (!response.ok) {
      const error = new Error(response.json.errorDescription);
      error.errorCode = response.json.errorCode;
      throw error;
    }
    const validatorFields = response.json;
    const hasRequiredField = Object.values(validatorFields).some((field) => field.required === true);
    if (hasRequiredField) yield put({ type: types.EXPAND_PANEL, isExpanded: true, panelName: 'claimInformation' });
    yield put({ type: types.VALIDATOR_FIELDS_SUCCESS, validatorFields });
    return { ok: true };
  } catch (error) {
    logError({
      message: 'Error in getValidatorFieldsSaga',
      error: error.message,
      errorCode: error.errorCode,
    });
    return { ok: false, error };
  }
}

export function* loadUpgradedReportDetailsSaga(upgradeReportId) {
  try {
    const upgradedReportResponse = yield call(upgradedReportService, upgradeReportId);
    if (!upgradedReportResponse.ok) {
      const error = new Error(upgradedReportResponse.json.errorDescription);
      error.errorCode = upgradedReportResponse.json.errorCode;
      throw error;
    }
    const upgradedReportDetails = upgradedReportResponse.json;
    yield put({ type: types.UPGRADED_REPORT_DETAILS_SUCCESS, upgradedReportDetails });
    history.unblock();
    history.push(UPGRADED_REPORT_ROUTE);
    return { ok: true, upgraded: true };
  } catch (error) {
    logError({
      message: 'Error in loadUpgradedReportDetailsSaga',
      error: error.message,
      errorCode: error.errorCode,
      upgradeReportId,
    });
    yield put({ type: types.UPGRADED_REPORT_DETAILS_FAILURE });
    return { ok: false, upgraded: true };
  }
}

function* getUpgradeReportDetailsSaga(upgradeReportId) {
  try {
    const response = yield call(upgradeReportService, upgradeReportId);
    if (!response.ok) {
      if (response.status === CLIENT_ERROR_RESPONSES.STATUS_UNAUTHORIZED
        || response.status === CLIENT_ERROR_RESPONSES.STATUS_BAD_REQUEST || response.status === CLIENT_ERROR_RESPONSES.STATUS_NOT_FOUND) {
        history.unblock();
        history.push(ORDER_ROUTE);
      } else if (response.status === CLIENT_ERROR_RESPONSES.STATUS_CONFLICT) {
        const upgradedReportDetailsResponse = yield call(loadUpgradedReportDetailsSaga, upgradeReportId);
        return upgradedReportDetailsResponse;
      } else {
        const error = new Error(response.json.errorDescription);
        error.errorCode = response.json.errorCode;
        throw error;
      }
    } else {
      const upgradeDetails = { reportId: upgradeReportId, ...response.json };
      yield put({ type: types.UPGRADE_REPORT_DETAILS_SUCCESS, upgradeDetails });
    }
    return { ok: true };
  } catch (error) {
    logError({
      message: 'Error in getUpgradeReportDetailsSaga',
      error: error.message,
      errorCode: error.errorCode,
      upgradeReportId,
    });
    return { ok: false, error };
  }
}

export function* loadFinalizeOrderSaga() {
  try {
    yield put({ type: types.LOADING_FINALIZE_ORDER });
    // Drop leftover upgradeFromReportId without wiping order-page subscriptionEstimatedTotal.
    yield put({ type: types.CLEAR_UPGRADE_FINALIZE_CONTEXT });
    yield put({ type: types.UPDATE_FOCUSED_POINT, focusedPointIndex: null });
    yield getInitParametersSaga();

    yield call(getUserProductsSaga);

    // Same order as normal flow: always load page data (order preferences, control states, billing, validator) after Next
    const pageDataResponses = yield all([
      call(getControlStates),
      call(getBillPayerInfo),
      call(getOrderPreferences),
      call(getValidatorFieldsSaga),
    ]);
    if (pageDataResponses.some(({ ok }) => !ok)) {
      const { error } = pageDataResponses.find(({ ok }) => !ok);
      throw error;
    }

    const existingPlaceOrderError = yield select(({ finalizeOrder }) => finalizeOrder.placeOrderError);
    if (!existingPlaceOrderError) {
      const productSetsResult = yield call(updateProductSetsSaga);
      const estimatedCostResult = yield call(getEstimatedCostSaga);
      if (!productSetsResult.ok || !estimatedCostResult.ok) {
        const failed = !productSetsResult.ok ? productSetsResult : estimatedCostResult;
        throw failed.error || new Error('Product sets or estimated cost failed');
      }
    }

    const orderPreferences = (yield select(({ userData }) => userData.orderPreferences)) ?? {};
    const substitutionsAndCustomizationValues = Object.fromEntries(
      Object.entries(orderPreferences).filter(([key]) => substitutionAndCustomizationAttribute.includes(key)),
    );
    yield put({ type: types.LOADED_FINALIZE_ORDER, orderPreferences, substitutionsAndCustomizationValues });
  } catch (error) {
    logError({
      message: 'Error in loadFinalizeOrderSaga',
      error: error.message,
      errorCode: error.errorCode,
    });
    yield put({ type: types.LOAD_FINALIZE_ORDER_ERROR, error: error.message });
  }
}

// #genai Preserve the existing first-failure behavior while keeping the upgrade saga focused.
function throwFirstPageDataError(pageDataResponses) {
  const failedPageData = pageDataResponses.find(({ ok }) => !ok);
  if (failedPageData) throw failedPageData.error;
}

export function* loadUpgradeReportFinalizeOrderSaga({ upgradeReportId }) {
  try {
    yield put({ type: types.LOADING_FINALIZE_ORDER });
    yield put({ type: types.UPDATE_FOCUSED_POINT, focusedPointIndex: null });
    yield getInitParametersSaga();

    yield call(getUserProductsSaga);

    const isSubscriptionDashboardV2Enabled = yield select(isSubscriptionDashboardV2EnabledSelector);
    if (isSubscriptionDashboardV2Enabled) {
      yield call(getSubscriptionDashboardDataSaga);
      yield call(mergePrimarySubscriptionProductsSaga);
    }

    const reportId = yield call(decodeAndDecrypt, upgradeReportId, KEY, INITIALIZATION_VECTOR);

    // Same order as normal flow: always load page data (order preferences, control states, billing, validator) after Next
    const placeOrderForOtherUser = yield select(({ finalizeOrder, userData }) => finalizeOrder.userId !== userData.initParameters.customerId);
    const pageDataResponses = yield all([
      call(getControlStates),
      call(getBillPayerInfo, placeOrderForOtherUser),
      call(getOrderPreferences),
      call(getValidatorFieldsSaga),
    ]);
    throwFirstPageDataError(pageDataResponses);

    const existingPlaceOrderError = yield select(({ finalizeOrder }) => finalizeOrder.placeOrderError);
    const upgradeDetailsResponse = existingPlaceOrderError
      ? null
      : (yield call(getUpgradeReportDetailsSaga, reportId));

    if (upgradeDetailsResponse && !upgradeDetailsResponse.upgraded) {
      if (!upgradeDetailsResponse.ok) throw upgradeDetailsResponse.error;

      const upgradeState = yield select(upgradeProductSetSelector);

      // #genai: Fail-safe — bail out to the order page when no valid upgrade path exists
      if (shouldRedirectFromUpgrade(upgradeState)) {
        yield put({ type: types.LOAD_FINALIZE_ORDER_ERROR, error: 'Report is not available for upgrade' });
        history.unblock();
        history.push(ORDER_ROUTE);
        return;
      }

      // #genai: ESX-only addition pins the current product and appends the ESX add-on
      const esxProductSets = buildEsxUpgradeProductSets(upgradeState);
      if (esxProductSets) yield put({ type: types.UPDATE_PRODUCT_SETS, productSets: esxProductSets });

      const estimatedCostResponse = yield call(getEstimatedCostSaga);
      if (!estimatedCostResponse.ok) throw estimatedCostResponse.error;
    }

    const orderPreferences = (yield select(({ userData }) => userData.orderPreferences)) ?? {};
    const substitutionsAndCustomizationValues = Object.fromEntries(
      Object.entries(orderPreferences).filter(([key]) => substitutionAndCustomizationAttribute.includes(key)),
    );
    yield put({ type: types.LOADED_FINALIZE_ORDER, orderPreferences, substitutionsAndCustomizationValues });
  } catch (error) {
    logError({
      message: 'Error in loadUpgradeReportFinalizeOrderSaga',
      error: error.message,
      errorCode: error.errorCode,
      upgradeReportId,
    });
    yield put(error.code === 5
      ? { type: types.UPGRADED_REPORT_NOT_FOUND }
      : { type: types.LOAD_FINALIZE_ORDER_ERROR, error: error.message });
  }
}
